<template>
  <div class="space-y-6">
    <!-- Financial Integration Banner -->
    <div 
      style="background: linear-gradient(135deg, #042f2e 0%, #111827 100%);"
      class="rounded-2xl p-5 text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-sm border border-teal-600/30"
    >
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 rounded-xl bg-teal-500/20 border border-teal-400/30 flex items-center justify-center shrink-0">
          <i class="fa fa-link text-xl text-teal-300" />
        </div>
        <div>
          <h3 class="font-bold text-base text-teal-50">Integración Financiera Automática con Compras y Gastos</h3>
          <p class="text-xs text-teal-200 mt-0.5 leading-relaxed">
            Cada vez que registras una liquidación de nómina, se genera automáticamente un egreso en 
            <strong class="text-white underline decoration-teal-400">/shoppings</strong> bajo la categoría <strong class="text-white">Nómina</strong>, manteniendo cuadrado el flujo de caja del Dashboard general.
          </p>
        </div>
      </div>
      <Link
        href="/shoppings?tab=spents"
        class="px-4 py-2.5 bg-teal-400 hover:bg-teal-300 text-gray-950 rounded-xl text-xs font-bold whitespace-nowrap transition-all shadow-md active:scale-95"
      >
        Ver en Egresos &rarr;
      </Link>
    </div>

    <!-- Bento Grid: Pending cuts by employee & Recent Settlements -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Left: Pendiente por Liquidar por Personal -->
      <div class="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="font-bold text-gray-900 text-base">Personal con Turnos Pendientes</h2>
            <p class="text-xs text-gray-500">Monto acumulado listo para corte y pago</p>
          </div>
          <button
            type="button"
            class="text-xs font-bold text-orange-700 hover:text-orange-800 bg-orange-50 hover:bg-orange-100 border border-orange-200 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
            @click="$emit('open-settlement-modal')"
          >
            Liquidar Todo
          </button>
        </div>

        <div v-if="employeesWithPendingShifts.length === 0" class="py-12 text-center text-gray-400">
          <i class="fa fa-check-circle text-3xl text-emerald-500 mb-2" />
          <p class="text-sm font-semibold text-gray-600">¡Al día! No hay turnos pendientes por liquidar.</p>
        </div>

        <div v-else class="divide-y divide-gray-100">
          <div 
            v-for="emp in employeesWithPendingShifts" 
            :key="emp.id"
            class="py-3.5 flex items-center justify-between gap-3 hover:bg-gray-50/50 rounded-xl px-2 transition-colors"
          >
            <div class="flex items-center gap-3 min-w-0">
              <div class="w-10 h-10 rounded-xl bg-orange-500 text-white font-bold text-sm flex items-center justify-center shrink-0 shadow-sm">
                {{ getInitials(emp.name) }}
              </div>
              <div class="truncate">
                <div class="font-bold text-gray-900 text-sm truncate">{{ emp.name }}</div>
                <div class="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                  <span>{{ emp.role_or_job || 'Personal' }}</span>
                  <span>•</span>
                  <span class="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800">
                    {{ emp.pending_shifts_count }} turnos
                  </span>
                </div>
              </div>
            </div>

            <div class="flex items-center gap-3 shrink-0">
              <div class="text-right">
                <div class="font-bold text-gray-900 text-base">{{ formatUSD(emp.pending_shifts_sum || 0) }}</div>
                <div class="text-xs text-gray-400">{{ formatBs(emp.pending_shifts_sum || 0) }}</div>
              </div>
              <button
                type="button"
                class="px-3.5 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-semibold transition-all shadow-sm active:scale-95 cursor-pointer"
                @click="$emit('open-settlement-modal', emp)"
              >
                Liquidar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Últimas Liquidaciones -->
      <div class="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="font-bold text-gray-900 text-base">Últimas Liquidaciones</h2>
            <p class="text-xs text-gray-500">Historial reciente de pagos efectuados</p>
          </div>
          <button
            type="button"
            class="text-xs font-bold text-orange-600 hover:text-orange-700 cursor-pointer"
            @click="$emit('change-tab', 'payments')"
          >
            Ver todo &rarr;
          </button>
        </div>

        <div v-if="!stats.recent_payments || stats.recent_payments.length === 0" class="py-12 text-center text-gray-400">
          <i class="fa fa-receipt text-3xl text-gray-300 mb-2" />
          <p class="text-sm font-semibold text-gray-600">Aún no se han registrado liquidaciones.</p>
        </div>

        <div v-else class="divide-y divide-gray-100">
          <div 
            v-for="pay in stats.recent_payments" 
            :key="pay.id"
            class="py-3 flex items-center justify-between gap-3 hover:bg-gray-50/50 rounded-xl px-2 transition-colors"
          >
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-xs font-mono font-bold text-gray-700 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded">
                  #{{ pay.reference_number }}
                </span>
                <span class="font-bold text-gray-900 text-sm truncate">{{ pay.employee?.name }}</span>
              </div>
              <div class="text-xs text-gray-500 mt-0.5">
                {{ formatDate(pay.payment_date) }} • {{ pay.payment_method }} • {{ pay.total_shifts }} turnos
              </div>
            </div>

            <div class="flex items-center gap-3 shrink-0">
              <span class="font-bold text-emerald-600 text-base">
                {{ formatUSD(pay.total_paid) }}
              </span>
              <a
                :href="`/paysheet/settlements/${pay.id}/receipt`"
                target="_blank"
                class="w-8 h-8 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 flex items-center justify-center transition-colors"
                title="Imprimir / Ver Comprobante"
              >
                <i class="fa fa-file-pdf text-sm text-red-600" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'

export default {
  name: 'PaysheetDashboardTab',

  components: {
    Link,
  },

  props: {
    stats: {
      type: Object,
      required: true,
    },
    employeesWithPendingShifts: {
      type: Array,
      default: () => [],
    },
    currentExchangeRate: {
      type: Number,
      default: 1.0,
    },
  },

  emits: ['open-settlement-modal', 'change-tab'],

  methods: {
    formatUSD(val) {
      const num = parseFloat(val) || 0
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    formatBs(val) {
      const num = (parseFloat(val) || 0) * (this.currentExchangeRate || 1)
      return num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Bs.'
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const parts = dateStr.split('T')[0].split('-')
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`
      }
      return dateStr
    },

    getInitials(name) {
      if (!name) return 'RG'
      return name
        .split(' ')
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    },
  },
}
</script>
